import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, differenceInDays } from "date-fns";
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Send,
  MoreHorizontal,
  CheckSquare,
  Square,
  AlertTriangle,
  Building2,
  Calendar,
  DollarSign,
  ChevronDown,
  FileDown,
  Printer,
  ArrowUpDown,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import ClaimDetailDialog from "@/components/billing/ClaimDetailDialog";
import { ClaimSubmissionForm, type ClaimFormData } from "@/components/billing/ClaimSubmissionForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// CPT code pricing reference
const cptPricing: Record<string, number> = {
  "99201": 75, "99202": 95, "99203": 125, "99211": 45, "99212": 85,
  "99213": 125, "99214": 150, "99215": 200, "90837": 175, "90834": 125,
  "90832": 85, "99457": 50, "99458": 40, "99453": 20, "99454": 65,
};

interface ClaimData {
  id: string;
  member_id: string;
  member_name: string;
  scheduled_date: string;
  visit_type: string;
  cpt_code: string | null;
  billing_status: string | null;
  billing_units: number | null;
  actual_duration_minutes: number | null;
  insurance_type: string;
  amount: number;
  provider_id: string;
}

type SortField = "scheduled_date" | "member_name" | "amount" | "billing_status";
type SortDirection = "asc" | "desc";

export default function ClaimsManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [payerFilter, setPayerFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [selectedClaims, setSelectedClaims] = useState<string[]>([]);
  const [selectedClaim, setSelectedClaim] = useState<ClaimData | null>(null);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [createClaimOpen, setCreateClaimOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>("scheduled_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch claims data
  const { data: claims, isLoading, refetch } = useQuery({
    queryKey: ["billing-claims-full"],
    queryFn: async () => {
      const { data: sessions, error: sessionsError } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .order("scheduled_date", { ascending: false });

      if (sessionsError) throw sessionsError;
      if (!sessions || sessions.length === 0) return [];

      const memberIds = [...new Set(sessions.map(s => s.member_id))];
      const { data: intakeForms } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name, insurance_type")
        .in("user_id", memberIds);

      const claimsData: ClaimData[] = sessions.map(session => {
        const intake = intakeForms?.find(i => i.user_id === session.member_id);
        const cptCode = session.cpt_code || getCptCodeFromVisitType(session.visit_type);
        const amount = cptPricing[cptCode] || 100;

        return {
          id: session.id,
          member_id: session.member_id,
          member_name: intake ? `${intake.first_name} ${intake.last_name}` : "Unknown Patient",
          scheduled_date: session.scheduled_date,
          visit_type: session.visit_type,
          cpt_code: cptCode,
          billing_status: session.billing_status || (session.status === "completed" ? "pending" : "unbilled"),
          billing_units: session.billing_units || 1,
          actual_duration_minutes: session.actual_duration_minutes,
          insurance_type: intake?.insurance_type || "medicare",
          amount: amount * (session.billing_units || 1),
          provider_id: session.provider_id,
        };
      });

      return claimsData;
    },
  });

  function getCptCodeFromVisitType(visitType: string): string {
    switch (visitType) {
      case "general": return "99213";
      case "followup": return "99212";
      case "urgent": return "99214";
      case "mental_health": return "90837";
      case "rpm": return "99457";
      default: return "99213";
    }
  }

  // Batch update mutation
  const batchUpdateMutation = useMutation({
    mutationFn: async ({ claimIds, status }: { claimIds: string[]; status: string }) => {
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ billing_status: status })
        .in("id", claimIds);

      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      const count = selectedClaims.length;
      toast({
        title: "Claims Updated",
        description: `${count} claim${count > 1 ? "s" : ""} updated to ${status}`,
      });
      setSelectedClaims([]);
      queryClient.invalidateQueries({ queryKey: ["billing-claims-full"] });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Create claim mutation
  const createClaimMutation = useMutation({
    mutationFn: async (data: ClaimFormData) => {
      const { data: session, error } = await supabase
        .from("telehealth_sessions")
        .insert({
          member_id: data.patientId,
          provider_id: data.patientId,
          scheduled_date: new Date().toISOString().split("T")[0],
          scheduled_time: new Date().toTimeString().split(" ")[0].substring(0, 5),
          visit_type: data.serviceType.toLowerCase().replace(" ", "_"),
          cpt_code: data.cptCodes[0]?.code || "99213",
          billing_status: "submitted",
          billing_units: data.cptCodes.length,
          status: "completed",
          notes: data.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return session;
    },
    onSuccess: () => {
      toast({ title: "Claim Created", description: "New claim has been submitted successfully." });
      setCreateClaimOpen(false);
      queryClient.invalidateQueries({ queryKey: ["billing-claims-full"] });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Filter and sort claims
  const filteredClaims = useMemo(() => {
    let result = claims || [];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.member_name.toLowerCase().includes(query) ||
        c.id.toLowerCase().includes(query) ||
        c.cpt_code?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter(c => c.billing_status === statusFilter);
    }

    // Payer filter
    if (payerFilter !== "all") {
      result = result.filter(c => c.insurance_type === payerFilter);
    }

    // Date range filter
    const today = new Date();
    if (dateRange === "7days") {
      result = result.filter(c => differenceInDays(today, new Date(c.scheduled_date)) <= 7);
    } else if (dateRange === "30days") {
      result = result.filter(c => differenceInDays(today, new Date(c.scheduled_date)) <= 30);
    } else if (dateRange === "90days") {
      result = result.filter(c => differenceInDays(today, new Date(c.scheduled_date)) <= 90);
    }

    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "scheduled_date":
          comparison = new Date(a.scheduled_date).getTime() - new Date(b.scheduled_date).getTime();
          break;
        case "member_name":
          comparison = a.member_name.localeCompare(b.member_name);
          break;
        case "amount":
          comparison = a.amount - b.amount;
          break;
        case "billing_status":
          comparison = (a.billing_status || "").localeCompare(b.billing_status || "");
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [claims, searchQuery, statusFilter, payerFilter, dateRange, sortField, sortDirection]);

  // Metrics
  const metrics = useMemo(() => {
    if (!claims) return { total: 0, pending: 0, approved: 0, denied: 0, submitted: 0, unbilled: 0 };
    return {
      total: claims.length,
      pending: claims.filter(c => c.billing_status === "pending").length,
      approved: claims.filter(c => c.billing_status === "approved").length,
      denied: claims.filter(c => c.billing_status === "denied").length,
      submitted: claims.filter(c => c.billing_status === "submitted").length,
      unbilled: claims.filter(c => !c.billing_status || c.billing_status === "unbilled").length,
    };
  }, [claims]);

  const handleSelectAll = () => {
    if (selectedClaims.length === filteredClaims.length) {
      setSelectedClaims([]);
    } else {
      setSelectedClaims(filteredClaims.map(c => c.id));
    }
  };

  const handleSelectClaim = (claimId: string) => {
    setSelectedClaims(prev =>
      prev.includes(claimId) ? prev.filter(id => id !== claimId) : [...prev, claimId]
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const exportToCSV = () => {
    const headers = ["Claim ID", "Patient", "Date", "CPT Code", "Amount", "Status", "Payer"];
    const rows = filteredClaims.map(c => [
      c.id,
      c.member_name,
      c.scheduled_date,
      c.cpt_code || "",
      c.amount.toString(),
      c.billing_status || "",
      c.insurance_type,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `claims-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "Export Complete", description: `Exported ${filteredClaims.length} claims to CSV` });
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-success/10 text-success hover:bg-success/10"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>;
      case "pending":
        return <Badge className="bg-warning/10 text-warning hover:bg-warning/10"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "denied":
        return <Badge className="bg-destructive/10 text-destructive hover:bg-destructive/10"><XCircle className="h-3 w-3 mr-1" />Denied</Badge>;
      case "submitted":
        return <Badge className="bg-accent/10 text-accent hover:bg-accent/10"><Send className="h-3 w-3 mr-1" />Submitted</Badge>;
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Unbilled</Badge>;
    }
  };

  const getPayerBadge = (type: string) => {
    const colors: Record<string, string> = {
      medicare: "bg-accent/10 text-accent",
      medicaid: "bg-purple-100 text-purple-700",
      dual_eligible: "bg-indigo-100 text-indigo-700",
    };
    return <Badge className={`${colors[type] || "bg-muted"} capitalize`}>{type.replace("_", " ")}</Badge>;
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Claims Management</h1>
                <p className="text-muted-foreground">Manage, track, and process insurance claims</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={exportToCSV}>
                      <FileDown className="h-4 w-4 mr-2" />
                      Export to CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => window.print()}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print Report
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Dialog open={createClaimOpen} onOpenChange={setCreateClaimOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-success hover:bg-success/90">
                      <Plus className="h-4 w-4 mr-2" />
                      New Claim
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create New Claim</DialogTitle>
                      <DialogDescription>Submit a new claim for billing and reimbursement.</DialogDescription>
                    </DialogHeader>
                    <ClaimSubmissionForm
                      onSubmit={(data) => createClaimMutation.mutate(data)}
                      onCancel={() => setCreateClaimOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Status Tabs */}
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "all", label: "All Claims", count: metrics.total },
                { value: "unbilled", label: "Unbilled", count: metrics.unbilled, color: "text-muted-foreground" },
                { value: "pending", label: "Pending", count: metrics.pending, color: "text-warning" },
                { value: "submitted", label: "Submitted", count: metrics.submitted, color: "text-accent" },
                { value: "approved", label: "Paid", count: metrics.approved, color: "text-success" },
                { value: "denied", label: "Denied", count: metrics.denied, color: "text-destructive" },
              ].map(tab => (
                <Button
                  key={tab.value}
                  variant={statusFilter === tab.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(tab.value)}
                  className={statusFilter === tab.value ? "" : tab.color}
                >
                  {tab.label}
                  <Badge variant="secondary" className="ml-2">{tab.count}</Badge>
                </Button>
              ))}
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by patient, claim ID, or CPT code..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={payerFilter} onValueChange={setPayerFilter}>
                    <SelectTrigger className="w-[160px]">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Payer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payers</SelectItem>
                      <SelectItem value="medicare">Medicare</SelectItem>
                      <SelectItem value="medicaid">Medicaid</SelectItem>
                      <SelectItem value="dual_eligible">Dual Eligible</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={dateRange} onValueChange={setDateRange}>
                    <SelectTrigger className="w-[160px]">
                      <Calendar className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Date Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="7days">Last 7 Days</SelectItem>
                      <SelectItem value="30days">Last 30 Days</SelectItem>
                      <SelectItem value="90days">Last 90 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Batch Actions */}
            {selectedClaims.length > 0 && (
              <Card className="border-primary/50 bg-primary/5">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span className="font-medium">{selectedClaims.length} claim{selectedClaims.length > 1 ? "s" : ""} selected</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => batchUpdateMutation.mutate({ claimIds: selectedClaims, status: "submitted" })}
                        disabled={batchUpdateMutation.isPending}
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Submit All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-success border-success/30 hover:bg-success/10"
                        onClick={() => batchUpdateMutation.mutate({ claimIds: selectedClaims, status: "approved" })}
                        disabled={batchUpdateMutation.isPending}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Mark Paid
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        onClick={() => batchUpdateMutation.mutate({ claimIds: selectedClaims, status: "denied" })}
                        disabled={batchUpdateMutation.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Mark Denied
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedClaims([])}>
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Claims Table */}
            <Card>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : filteredClaims.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">No claims found</h3>
                    <p className="text-muted-foreground">Try adjusting your filters or create a new claim</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedClaims.length === filteredClaims.length && filteredClaims.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" onClick={() => handleSort("member_name")} className="gap-1">
                            Patient
                            <ArrowUpDown className="h-3 w-3" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" onClick={() => handleSort("scheduled_date")} className="gap-1">
                            Service Date
                            <ArrowUpDown className="h-3 w-3" />
                          </Button>
                        </TableHead>
                        <TableHead>CPT Code</TableHead>
                        <TableHead>Payer</TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" onClick={() => handleSort("amount")} className="gap-1">
                            Amount
                            <ArrowUpDown className="h-3 w-3" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button variant="ghost" size="sm" onClick={() => handleSort("billing_status")} className="gap-1">
                            Status
                            <ArrowUpDown className="h-3 w-3" />
                          </Button>
                        </TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClaims.map((claim) => (
                        <TableRow
                          key={claim.id}
                          className={selectedClaims.includes(claim.id) ? "bg-primary/5" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedClaims.includes(claim.id)}
                              onCheckedChange={() => handleSelectClaim(claim.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{claim.member_name}</p>
                              <p className="text-xs text-muted-foreground font-mono">{claim.id.slice(0, 8)}...</p>
                            </div>
                          </TableCell>
                          <TableCell>{format(new Date(claim.scheduled_date), "MMM d, yyyy")}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">{claim.cpt_code}</Badge>
                          </TableCell>
                          <TableCell>{getPayerBadge(claim.insurance_type)}</TableCell>
                          <TableCell className="font-semibold">${claim.amount.toLocaleString()}</TableCell>
                          <TableCell>{getStatusBadge(claim.billing_status)}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => { setSelectedClaim(claim); setClaimDialogOpen(true); }}>
                                  <FileText className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => batchUpdateMutation.mutate({ claimIds: [claim.id], status: "submitted" })}
                                  disabled={claim.billing_status === "submitted"}
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit to Payer
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => batchUpdateMutation.mutate({ claimIds: [claim.id], status: "approved" })}
                                  className="text-success"
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  Mark as Paid
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => batchUpdateMutation.mutate({ claimIds: [claim.id], status: "denied" })}
                                  className="text-destructive"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Mark as Denied
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Summary Footer */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Showing {filteredClaims.length} of {claims?.length || 0} claims</span>
              <span>
                Total Amount: <span className="font-semibold text-foreground">${filteredClaims.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}</span>
              </span>
            </div>
          </div>
        </main>
      </div>

      <ClaimDetailDialog
        claim={selectedClaim}
        open={claimDialogOpen}
        onOpenChange={setClaimDialogOpen}
      />
    </div>
  );
}
